// Renderer-liveness heartbeat for idanative's layered freeze-detection (Phase A
// emitter). idanative stamps `rendererTs` on receipt and reports a
// `subtype:"renderer"` freeze when these beats go stale (~4 missed beats).
// Cross-repo design: idanative docs/HEARTBEATS.md.

export const RENDERER_BEAT_MS = 3000;

/**
 * Start emitting a renderer-main-thread heartbeat to the native host.
 *
 * The beat MUST originate from something that also freezes when the UI freezes,
 * so we drive it with requestAnimationFrame (paced down to ~3s with a timestamp
 * check): rAF is tied to the render loop and stalls exactly when rendering
 * stalls. A Web Worker or any off-main-thread timer would keep beating straight
 * through a UI freeze and falsely report "alive" — defeating the feature.
 *
 * rAF also pauses while the app is backgrounded, so beats only flow while
 * foregrounded, which is what the native side expects.
 *
 * No-op outside the native host (no ReactNativeWebView bridge), so there is no
 * behavioral impact or wasted loop in a plain browser.
 *
 * @returns {() => void} stop function that cancels the loop.
 */
export function startRendererHeartbeat({
    beatMs = RENDERER_BEAT_MS,
    win = typeof window !== "undefined" ? window : undefined,
} = {}) {
    const bridge = win && win.ReactNativeWebView;
    if (!bridge || typeof win.requestAnimationFrame !== "function") {
        return () => {};
    }

    let rafId;
    let lastBeat = null;
    let stopped = false;

    const loop = now => {
        if (stopped) {
            return;
        }
        if (lastBeat === null || now - lastBeat >= beatMs) {
            lastBeat = now;
            try {
                bridge.postMessage(JSON.stringify({ type: "heartbeat", t: Date.now() }));
            } catch (e) {
                // Never let a transient bridge error kill the loop — mirrors
                // idanative Analytics' "never throw into the app" discipline.
            }
        }
        rafId = win.requestAnimationFrame(loop);
    };

    rafId = win.requestAnimationFrame(loop);

    return () => {
        stopped = true;
        if (rafId != null && typeof win.cancelAnimationFrame === "function") {
            win.cancelAnimationFrame(rafId);
        }
    };
}
